'use client'

import * as React from 'react'
import { Plus, Search, AlertCircle, CheckCircle, Loader2, XCircle, MoreHorizontal } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { cn } from 'lib/utils'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../firebase'

const severityConfig= {
  low: { color: 'text-success', bg: 'bg-success/15 text-success' },
  medium: { color: 'text-warning', bg: 'bg-warning/15 text-warning' },
  high: { color: 'text-destructive', bg: 'bg-destructive/15 text-destructive' },
  critical: { color: 'text-destructive', bg: 'bg-destructive text-primary-foreground' },
}

const statusConfig= {
  open: { icon: <AlertCircle className="size-3" />, bg: 'bg-destructive/15 text-destructive' },
  'in-progress': { icon: <Loader2 className="size-3" />, bg: 'bg-primary/15 text-primary' },
  resolved: { icon: <CheckCircle className="size-3" />, bg: 'bg-success/15 text-success' },
  closed: { icon: <XCircle className="size-3" />, bg: 'bg-muted text-muted-foreground' },
}

function formatDate(value) {
  if (!value) return '—'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })
}

export default function IssuesPage() {
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [severityFilter, setSeverityFilter] = React.useState('all')
  const [issues, setIssues] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadIssues() {
      try {
        const [issueSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'issues')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        setIssues(issueSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setIssues([])
        setProjects([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadIssues()

    return () => {
      isMounted = false
    }
  }, [])

  const projectMap = projects.reduce((acc, project) => {
    acc[project.id] = project
    return acc
  }, {})

  const normalizedIssues = issues.map((issue) => ({
    ...issue,
    title: issue.title || issue.subject || 'Issue',
    assignee: issue.assigneeName || issue.assignee || issue.ownerName || 'Unassigned',
    category: issue.category || 'General',
    severity: issue.severity || 'medium',
    status: issue.status || 'open',
    raisedDate: issue.createdAt || issue.raisedDate,
  }))

  const filtered = normalizedIssues.filter((issue) => {
    const project = projectMap[issue.projectId]
    const projectName = `${project?.code || ''} ${project?.title || ''}`.toLowerCase()
    const ms =
      issue.title.toLowerCase().includes(search.toLowerCase()) ||
      issue.assignee.toLowerCase().includes(search.toLowerCase()) ||
      issue.category.toLowerCase().includes(search.toLowerCase()) ||
      projectName.includes(search.toLowerCase())
    const mst = statusFilter === 'all' || issue.status === statusFilter
    const msv = severityFilter === 'all' || issue.severity === severityFilter
    return ms && mst && msv
  })

  const totalIssues = normalizedIssues.length
  const openCount = normalizedIssues.filter((issue) => issue.status === 'open').length
  const inProgressCount = normalizedIssues.filter((issue) => issue.status === 'in-progress').length
  const resolvedCount = normalizedIssues.filter((issue) => issue.status === 'resolved' || issue.status === 'closed').length

  const categoryCounts = normalizedIssues.reduce((acc, issue) => {
    acc[issue.category] = (acc[issue.category] || 0) + 1
    return acc
  }, {})
  const categoryData = Object.entries(categoryCounts).map(([name, count]) => ({ name, count }))

  const statusPie = [
    { name: 'Open', value: openCount, color: 'oklch(0.577 0.245 27.325)' },
    { name: 'In Progress', value: inProgressCount, color: 'oklch(0.35 0.12 145)' },
    { name: 'Resolved', value: normalizedIssues.filter(i => i.status === 'resolved').length, color: 'oklch(0.55 0.16 150)' },
    { name: 'Closed', value: normalizedIssues.filter(i => i.status === 'closed').length, color: 'oklch(0.87 0.15 85)' },
  ]

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Issues Tracker</h1>
          <p className="text-sm text-muted-foreground mt-1">Track, assign, and resolve project issues across the portfolio</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : totalIssues}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Total Issues</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-destructive">{loading ? '—' : openCount}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Open</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{loading ? '—' : inProgressCount}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">In Progress</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : resolvedCount}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Resolved/Closed</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Issues by Category</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={categoryData} barSize={22}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="count" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10, fill: 'oklch(0.48 0.03 145)' }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Status Distribution</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={statusPie} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3} strokeWidth={0}>
                  {statusPie.map(e => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {statusPie.map(e => (
                <div key={e.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: e.color }} />
                  <span className="text-[10px] text-muted-foreground">
                    {e.name} ({e.value})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search issues..." className="pl-10 h-9" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="open">Open</SelectItem><SelectItem value="in-progress">In Progress</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent></Select>
        <Select value={severityFilter} onValueChange={setSeverityFilter}><SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Severity" /></SelectTrigger><SelectContent><SelectItem value="all">All Severity</SelectItem><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select>
      </div>

      {/* Table */}
      <Card className="border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Issue</TableHead>
              <TableHead className="text-xs">Project</TableHead>
              <TableHead className="text-xs">Severity</TableHead>
              <TableHead className="text-xs">Assignee</TableHead>
              <TableHead className="text-xs">Raised</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                  Loading issues...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                  No issues found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((issue) => {
                const project = projectMap[issue.projectId]
                return (
                  <TableRow key={issue.id} className={cn(issue.severity === 'critical' && 'bg-destructive/5')}>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-foreground">{issue.title}</span>
                        <span className="text-[10px] text-muted-foreground">{issue.category}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {project?.code || project?.shortCode || project?.title || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('text-[10px] capitalize', severityConfig[issue.severity]?.bg)}>
                        {issue.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{issue.assignee}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(issue.raisedDate)}</TableCell>
                    <TableCell>
                      <Badge className={cn('text-[10px] capitalize gap-1', statusConfig[issue.status]?.bg)}>
                        {statusConfig[issue.status]?.icon}
                        {issue.status.replace('-', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-7">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>View Details</DropdownMenuItem>
                          <DropdownMenuItem>Update Status</DropdownMenuItem>
                          <DropdownMenuItem>Reassign</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}
